import React, { useState } from "react";
import { Warehouse as WHIcon, MapPin, Layers, Box, CheckCircle2, Search, ArrowRight } from "lucide-react";
import { useApp } from "../../app/context/AppContext";
import { calculateWarehouseOccupancy } from "../../utils/calculations";

export function WarehouseMap({ warehouse, onSelectBin }) {
  const { batches, products } = useApp();
  const [selectedBin, setSelectedBin] = useState(null);

  const occupancy = calculateWarehouseOccupancy(warehouse);

  const handleBinClick = (bin) => {
    // Find all batches placed in this bin
    const binBatches = batches.filter((b) => b.binCode === bin.code && b.warehouseId === warehouse.id);
    const enriched = binBatches.map((b) => {
      const p = products.find((prod) => prod.id === b.productId);
      return { ...b, product: p };
    });

    const info = {
      ...bin,
      batches: enriched
    };
    setSelectedBin(info);
    if (onSelectBin) onSelectBin(info);
  };

  return (
    <div className="space-y-5">
      {/* Warehouse Overview Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 text-xs">
        <div>
          <h4 className="text-base font-bold text-slate-900 dark:text-white font-display">
            {warehouse.name}
          </h4>
          <p className="text-slate-500 mt-0.5">{warehouse.address} • Manager: {warehouse.manager}</p>
        </div>

        <div className="flex items-center gap-4">
          <div>
            <p className="text-slate-400">Total Capacity:</p>
            <p className="font-bold text-slate-900 dark:text-white font-mono">{occupancy.totalCapacity} units</p>
          </div>
          <div>
            <p className="text-slate-400">Current Occupancy:</p>
            <p className="font-bold text-emerald-600 dark:text-emerald-400 font-mono">
              {occupancy.percentage}% ({occupancy.currentCount} units)
            </p>
          </div>
        </div>
      </div>

      {/* Visual Hierarchy Grid: Section -> Rack -> Shelf -> Bin */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Visual Map Grid */}
        <div className="lg:col-span-2 space-y-4">
          {warehouse.sections?.map((sec) => (
            <div
              key={sec.id}
              className="p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 space-y-3 shadow-xs"
            >
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-2">
                <span className="font-bold text-slate-900 dark:text-white text-xs font-display">
                  {sec.name}
                </span>
                <span className="font-mono text-[10px] text-slate-400">{sec.code}</span>
              </div>

              {sec.racks?.map((rack) => (
                <div key={rack.id} className="space-y-2">
                  <p className="text-[11px] font-semibold text-slate-500">{rack.name}</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {rack.shelves?.map((shelf) => (
                      <div
                        key={shelf.id}
                        className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200/60 dark:border-slate-800 space-y-1.5"
                      >
                        <p className="text-[10px] text-slate-400 font-mono">{shelf.name}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {shelf.bins?.map((bin) => {
                            const isSelected = selectedBin?.code === bin.code;
                            const isFull = bin.currentCount >= bin.maxCapacity;
                            const isEmpty = bin.currentCount === 0;

                            return (
                              <button
                                key={bin.id}
                                onClick={() => handleBinClick(bin)}
                                className={`px-2.5 py-1.5 rounded-lg text-[11px] font-mono font-bold transition flex items-center gap-1 shadow-xs ${
                                  isSelected
                                    ? "bg-emerald-600 text-white ring-2 ring-emerald-400"
                                    : isFull
                                    ? "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300 border border-rose-300 dark:border-rose-900"
                                    : isEmpty
                                    ? "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border border-dashed border-slate-300 dark:border-slate-700"
                                    : "bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800"
                                }`}
                              >
                                <Box className="w-3 h-3" />
                                {bin.code}
                                <span className="text-[9px] opacity-80">
                                  ({bin.currentCount}/{bin.maxCapacity})
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Selected Bin Inspector Panel */}
        <div className="rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm space-y-4 text-xs">
          <div className="border-b border-slate-100 dark:border-slate-800 pb-3">
            <h4 className="text-sm font-bold text-slate-900 dark:text-white font-display flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-emerald-500" />
              Bin Inspector & Contents
            </h4>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Click any bin in the visual rack map to inspect what is stored
            </p>
          </div>

          {!selectedBin ? (
            <div className="py-12 text-center text-slate-400">
              <Box className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p>Select a bin from the warehouse map.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 space-y-1">
                <div className="flex justify-between font-mono font-bold text-slate-900 dark:text-white">
                  <span>Bin Code:</span>
                  <span className="text-emerald-600 dark:text-emerald-400">{selectedBin.code}</span>
                </div>
                <div className="flex justify-between text-slate-500">
                  <span>Max Capacity:</span>
                  <span className="font-mono">{selectedBin.maxCapacity} units</span>
                </div>
                <div className="flex justify-between text-slate-500">
                  <span>Current Utilization:</span>
                  <span className="font-mono font-semibold">
                    {selectedBin.currentCount} units ({Math.round((selectedBin.currentCount / selectedBin.maxCapacity) * 100)}%)
                  </span>
                </div>
              </div>

              <div>
                <p className="font-semibold text-slate-900 dark:text-white mb-2">Stored Product Batches:</p>
                {selectedBin.batches?.length === 0 ? (
                  <p className="text-slate-400 italic">Bin is currently empty. Ready for putaway.</p>
                ) : (
                  <div className="space-y-2">
                    {selectedBin.batches.map((b) => (
                      <div
                        key={b.id}
                        className="p-2.5 bg-emerald-50/50 dark:bg-emerald-950/20 rounded-lg border border-emerald-200/50 dark:border-emerald-900/50"
                      >
                        <p className="font-bold text-slate-900 dark:text-white line-clamp-1">
                          {b.product?.name || b.productName}
                        </p>
                        <div className="flex justify-between text-[11px] text-slate-500 mt-1 font-mono">
                          <span>Batch: {b.batchNo}</span>
                          <span className="font-bold text-emerald-600 dark:text-emerald-400">
                            {b.qtyRemaining} units
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
